
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { 
  ChevronRight, 
  Save, 
  FileImage,
  X, 
  Loader2
} from 'lucide-react';
import { CertificateTemplate, CertificateTemplateType } from '@/types/certificateTemplate';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { certificateTemplateService } from '@/services/certification/templates';

interface BasicInfoFormData {
  name: string;
  description: string;
  type: CertificateTemplateType;
  backgroundColor: string;
  backgroundImage?: string;
  isDefault: boolean;
}

const EditTemplateBasicInfoPage: React.FC = () => {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const [template, setTemplate] = useState<CertificateTemplate | null>(null);
  const [backgroundPreview, setBackgroundPreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  
  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm<BasicInfoFormData>({
    defaultValues: {
      name: '',
      description: '',
      type: 'course-completion',
      backgroundColor: '#ffffff',
      isDefault: false
    }
  });

  useEffect(() => {
    if (!templateId) {
      toast.error('Template ID is missing');
      navigate('/certificate-templates');
      return;
    }
    
    // Load template data
    try {
      const loadedTemplate = certificateTemplateService.getCertificateTemplateById(templateId);
      if (loadedTemplate) {
        setTemplate(loadedTemplate);
        // Set form values
        setValue('name', loadedTemplate.name);
        setValue('description', loadedTemplate.description || '');
        setValue('type', loadedTemplate.type);
        setValue('backgroundColor', loadedTemplate.backgroundColor);
        setValue('isDefault', loadedTemplate.isDefault || false);
        
        if (loadedTemplate.backgroundImage) {
          setBackgroundPreview(loadedTemplate.backgroundImage);
        }
        
        // Store in session for the multi-step flow
        sessionStorage.setItem('editTemplate', JSON.stringify(loadedTemplate));
      } else {
        toast.error('Template not found');
        navigate('/certificate-templates');
      }
    } catch (error) {
      console.error('Error loading template:', error);
      toast.error('Failed to load template data');
    } finally {
      setLoading(false);
    }
  }, [templateId, navigate, setValue]);

  const onSubmit = (data: BasicInfoFormData) => {
    if (!templateId || !template) return;
    
    // Update the stored template with new basic info
    const updatedTemplate = {
      ...template,
      ...data,
      backgroundImage: backgroundPreview
    };
    
    // Store in session storage for the workflow
    sessionStorage.setItem('editTemplate', JSON.stringify(updatedTemplate));
    
    toast.success('Basic information saved');
    navigate(`/certificate-templates/edit/${templateId}/upload`);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Create a local URL for the image
    const fileUrl = URL.createObjectURL(file);
    setBackgroundPreview(fileUrl);
    setValue('backgroundImage', fileUrl);
  };

  const handleCancel = () => {
    navigate('/certificate-templates');
  };

  if (loading) {
    return (
      <div className="container mx-auto py-8 flex items-center justify-center h-[50vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8">
      <Helmet>
        <title>Edit Certificate Template - Basic Info | NALC</title>
      </Helmet>
      
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Edit Certificate Template</h1>
        <p className="text-muted-foreground mt-2">
          Step 1: Update basic information for your certificate template
        </p>
      </div>
      
      <div className="flex justify-center mb-6">
        <div className="flex items-center">
          <div className="flex flex-col items-center">
            <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
              1
            </div>
            <span className="text-sm mt-1 text-primary font-medium">Basic Info</span>
          </div>
          <div className="w-12 h-1 bg-muted mx-1"></div>
          <div className="flex flex-col items-center">
            <div className="w-10 h-10 rounded-full bg-muted text-muted-foreground flex items-center justify-center">
              2
            </div>
            <span className="text-sm mt-1">PDF Upload</span>
          </div>
          <div className="w-12 h-1 bg-muted mx-1"></div>
          <div className="flex flex-col items-center">
            <div className="w-10 h-10 rounded-full bg-muted text-muted-foreground flex items-center justify-center">
              3
            </div>
            <span className="text-sm mt-1">Field Mapping</span>
          </div>
          <div className="w-12 h-1 bg-muted mx-1"></div>
          <div className="flex flex-col items-center">
            <div className="w-10 h-10 rounded-full bg-muted text-muted-foreground flex items-center justify-center">
              4
            </div>
            <span className="text-sm mt-1">Preview</span>
          </div>
        </div>
      </div>
      
      <Card>
        <CardHeader>
          <CardTitle>Template Basic Information</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <Label htmlFor="name">Template Name <span className="text-red-500">*</span></Label>
                  <Input
                    id="name"
                    placeholder="Enter template name"
                    {...register('name', { required: 'Template name is required' })}
                    className="mt-1"
                  />
                  {errors.name && (
                    <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
                  )}
                </div>
                
                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    placeholder="Enter description"
                    {...register('description')}
                    className="mt-1"
                    rows={3}
                  />
                </div>
                
                <div>
                  <Label htmlFor="type">Certificate Type <span className="text-red-500">*</span></Label>
                  <Select
                    value={watch('type')}
                    onValueChange={(value) => setValue('type', value as CertificateTemplateType)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select certificate type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="course-completion">Course Completion</SelectItem>
                      <SelectItem value="enrollment">Enrollment</SelectItem>
                      <SelectItem value="professional-development">Professional Development</SelectItem>
                      <SelectItem value="custom">Custom</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="backgroundColor">Background Color</Label>
                  <div className="flex mt-1">
                    <Input
                      id="backgroundColor"
                      type="color"
                      {...register('backgroundColor')}
                      className="w-12 h-10 p-1"
                    />
                    <Input
                      type="text"
                      value={watch('backgroundColor')}
                      onChange={(e) => setValue('backgroundColor', e.target.value)}
                      className="ml-2 flex-1"
                    />
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Switch
                    id="isDefault"
                    checked={watch('isDefault')}
                    onCheckedChange={(checked) => setValue('isDefault', checked)}
                  />
                  <Label htmlFor="isDefault">Set as default template for this type</Label>
                </div>
              </div>
              
              <div>
                <Label>Background Image (Optional)</Label>
                <div className="mt-1 border-2 border-dashed rounded-lg p-6 flex flex-col items-center justify-center">
                  {backgroundPreview ? (
                    <div className="relative w-full">
                      <img
                        src={backgroundPreview}
                        alt="Background Preview"
                        className="w-full h-auto rounded border"
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        className="absolute top-2 right-2 h-8 w-8"
                        onClick={() => {
                          setBackgroundPreview(null);
                          setValue('backgroundImage', undefined);
                        }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <>
                      <FileImage className="h-12 w-12 text-muted-foreground mb-2" />
                      <p className="text-sm text-muted-foreground mb-2">
                        Drag and drop or click to upload a background image
                      </p>
                      <Label
                        htmlFor="background-upload"
                        className="bg-primary text-primary-foreground hover:bg-primary/90 py-2 px-4 rounded cursor-pointer"
                      >
                        Choose Image
                      </Label>
                      <input
                        id="background-upload"
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        className="hidden"
                      />
                    </>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex justify-between pt-4 border-t">
              <Button type="button" variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button type="submit">
                Continue <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default EditTemplateBasicInfoPage;
